import React, { FC, useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';
import { formatDate } from 'utils/format';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    Paper,
    Box,
    Typography,
    Link,
    Tooltip,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { ScheduledStep, ScheduleStatus } from 'models/production-planning';
import {
    isSameDay,
    SCHEDULE_COLORS,
    SCHEDULE_BALL_SIZE as BALL_SIZE,
    SCHEDULE_LINE_HEIGHT as LINE_HEIGHT,
    SCHEDULE_ARROW_SIZE as ARROW_SIZE,
    getStartHour,
    getEndHour,
    getLabHourRange,
    isSpanFromYesterday,
    isSpanToTomorrow,
} from '../utils';
import PageTitle from 'components/shared/page-title/page-title.component';

type ProductionDashboardStepScheduleProps = {
    stepId: string;
    stepName: string;
    departmentName: string;
    date: Date;
    scheduledSteps: ScheduledStep[];
    onBack: () => void;
};

type SpanCellContentProps = {
    hour: number;
    visibleStartHour: number;
    visibleEndHour: number;
    continuesBefore: boolean;
    continuesAfter: boolean;
    color: string;
    stepName: string;
    startTime: Date;
    endTime: Date;
};

const centeredAbsolute = {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1,
} as const;

const SpanCellContent: FC<SpanCellContentProps> = ({
    hour,
    visibleStartHour,
    visibleEndHour,
    continuesBefore,
    continuesAfter,
    color,
    stepName,
    startTime,
    endTime,
}) => {
    const baseSx = { p: 0, height: 36, position: 'relative' as const };

    const isInSpan = hour >= visibleStartHour && hour <= visibleEndHour;
    if (!isInSpan) return <TableCell sx={baseSx} />;

    const isStartCell = hour === visibleStartHour;
    const isEndCell = hour === visibleEndHour;
    const isSameCell = isStartCell && isEndCell;

    const ball = <Box sx={{ width: BALL_SIZE, height: BALL_SIZE, borderRadius: '50%', backgroundColor: color }} />;

    const renderEndpoint = (type: 'start' | 'end') => {
        const extendsOutside = type === 'start' ? continuesBefore : continuesAfter;
        if (extendsOutside) {
            return <ChevronRightIcon sx={{ ...centeredAbsolute, color, fontSize: ARROW_SIZE }} />;
        }
        return (
            <Box
                sx={{
                    ...centeredAbsolute,
                    width: BALL_SIZE,
                    height: BALL_SIZE,
                    borderRadius: '50%',
                    backgroundColor: color,
                }}
            />
        );
    };

    const content = isSameCell ? (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {continuesBefore || continuesAfter ? <ChevronRightIcon sx={{ color, fontSize: ARROW_SIZE }} /> : ball}
        </Box>
    ) : (
        <Box sx={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center' }}>
            <Box
                sx={{
                    position: 'absolute',
                    left: isStartCell ? '50%' : 0,
                    right: isEndCell ? '50%' : 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: LINE_HEIGHT,
                    backgroundColor: color,
                }}
            />
            {isStartCell && renderEndpoint('start')}
            {isEndCell && renderEndpoint('end')}
        </Box>
    );

    const tooltipTitle = `${stepName}\n${formatDate(startTime, 'datetime')} - ${formatDate(endTime, 'datetime')}`;

    return (
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipTitle}</span>} arrow placement="top">
            <TableCell sx={baseSx}>{content}</TableCell>
        </Tooltip>
    );
};

const ProductionDashboardStepSchedule: FC<ProductionDashboardStepScheduleProps> = ({
    stepId,
    stepName,
    departmentName,
    date,
    scheduledSteps,
    onBack,
}) => {
    const { t: translate } = useTranslation(['production-dashboard']);

    const stepOrders = useMemo(
        () =>
            scheduledSteps
                .filter((step) => {
                    // By step id: the plan keeps the step name from when it was planned (DD-3786).
                    if (step.stepId !== stepId || step.department !== departmentName) return false;
                    const deadlineOnDay = isSameDay(step.deadline, date);
                    const startOnDay = step.startDate ? isSameDay(step.startDate, date) : false;
                    const spansDay = step.startDate != null && step.startDate <= date && step.deadline >= date;
                    return deadlineOnDay || startOnDay || spansDay;
                })
                .sort((a, b) => {
                    const aTime = (a.startDate ?? a.deadline).getTime();
                    const bTime = (b.startDate ?? b.deadline).getTime();
                    return aTime - bTime;
                }),
        [scheduledSteps, stepId, departmentName, date],
    );

    const openingHours = useReactiveVar(currentOrgVar)?.openingHours;

    // Columns stay within the lab's opening hours; bars that run earlier/later (e.g.
    // overnight machine time) are clamped to the edge and flagged with a continuation arrow.
    const hours = useMemo(() => {
        const { open, close } = getLabHourRange(date, openingHours);
        const range: number[] = [];
        for (let hour = open; hour < close; hour++) {
            range.push(hour);
        }
        return range;
    }, [date, openingHours]);

    const formatHour = (hour: number) => `${hour.toString().padStart(2, '0')}:00`;

    return (
        <>
            <Box mb={2}>
                <PageTitle
                    headingStyle="h2"
                    title={`${translate('production-dashboard:SCHEDULE')}: ${stepName} - ${formatDate(date, 'date')}`}
                    showBackButton
                    onBackArrow={onBack}
                    disableGutter
                />
            </Box>
            {stepOrders.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {translate('production-dashboard:NO_SCHEDULED_STEPS')}
                </Typography>
            ) : (
                <>
                    <Box display="flex" gap={2} mb={1}>
                        {(
                            [
                                ScheduleStatus.ON_SCHEDULE,
                                ScheduleStatus.EARLY,
                                ScheduleStatus.LATE,
                                ScheduleStatus.PAUSED,
                            ] as ScheduleStatus[]
                        ).map((status) => (
                            <Box key={status} display="flex" alignItems="center" gap={0.75}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: 24,
                                        height: 16,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: 0,
                                            right: 0,
                                            height: LINE_HEIGHT,
                                            backgroundColor: SCHEDULE_COLORS[status],
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: '50%',
                                            top: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: SCHEDULE_COLORS[status],
                                        }}
                                    />
                                </Box>
                                <Typography variant="caption">
                                    {status === ScheduleStatus.EARLY
                                        ? translate('production-dashboard:STATUS_DONE')
                                        : translate(`production-dashboard:STATUS_${status}`)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ minWidth: 200 }}>
                                        {translate('production-dashboard:ORDER_ID')}
                                    </TableCell>
                                    {hours.map((hour) => (
                                        <TableCell key={hour} align="center" sx={{ minWidth: 52 }}>
                                            {formatHour(hour)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stepOrders.map((step) => {
                                    const first = hours[0];
                                    const last = hours[hours.length - 1];
                                    const rawStartHour = getStartHour(step, date);
                                    const rawEndHour = getEndHour(step, date);
                                    // Full-clamp into the visible window so the span never inverts (and so a
                                    // bar lying entirely outside opening hours collapses onto the nearest edge).
                                    const visibleStartHour = Math.min(Math.max(rawStartHour, first), last);
                                    const visibleEndHour = Math.min(Math.max(rawEndHour, first), last);
                                    // Continues past the window only when it crosses a day boundary or its actual
                                    // start/end time lands outside opening hours — a deadline exactly at closing
                                    // time (last + 1) ends within the day and keeps a ball, not an arrow.
                                    const startsBeforeOpen =
                                        !!step.startDate &&
                                        isSameDay(step.startDate, date) &&
                                        step.startDate.getHours() + step.startDate.getMinutes() / 60 < first;
                                    const endsAfterClose =
                                        isSameDay(step.deadline, date) &&
                                        step.deadline.getHours() + step.deadline.getMinutes() / 60 > last + 1;
                                    const continuesBefore = isSpanFromYesterday(step, date) || startsBeforeOpen;
                                    const continuesAfter = isSpanToTomorrow(step, date) || endsAfterClose;
                                    const color = SCHEDULE_COLORS[step.scheduleStatus];

                                    return (
                                        <TableRow key={step.orderId + step.stepId}>
                                            <TableCell
                                                sx={{
                                                    left: 0,
                                                    backgroundColor: '#fff',
                                                    zIndex: 1,
                                                    fontWeight: 500,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <Link
                                                    href={`/order-details/${step.orderId}/main-view`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    underline="hover"
                                                >
                                                    {[
                                                        step.orderDisplayId || step.orderId,
                                                        step.clinicName,
                                                        step.patientName,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' - ')}
                                                </Link>
                                            </TableCell>
                                            {hours.map((hour) => (
                                                <SpanCellContent
                                                    key={hour}
                                                    hour={hour}
                                                    visibleStartHour={visibleStartHour}
                                                    visibleEndHour={visibleEndHour}
                                                    continuesBefore={continuesBefore}
                                                    continuesAfter={continuesAfter}
                                                    color={color}
                                                    stepName={stepName}
                                                    startTime={step.startDate ?? step.deadline}
                                                    endTime={step.deadline}
                                                />
                                            ))}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </>
    );
};

export default ProductionDashboardStepSchedule;
